// ---------------------------------------------------------------------------
// Menu tree builder — flat list → nested tree
// ---------------------------------------------------------------------------
//
// Cycle detection policy (deterministic):
//   - For each node, walk the parent_id chain. If any ancestor appears twice,
//     the node that closes the loop is dropped (not the entire cycle).
//   - Orphans (parent not found) are also dropped after warning.
//   - Warnings include full parent chain for debuggability.
//   - This policy is intentional. Do not change without understanding impact.

/**
 * Build a menu tree from a flat list of menu items.
 * Does not mutate the input array.
 *
 * @param {import('../../contracts/menu.js').MenuItem[]} items
 * @param {(msg: string, data: unknown) => void} [onWarning]
 * @returns {import('../../contracts/menu.js').MenuNode[]}
 */
export function buildMenuTree(items, onWarning) {
  /** @type {Map<string, import('../../contracts/menu.js').MenuItem>} */
  const itemMap = new Map();
  for (const item of items) {
    itemMap.set(item.id, item);
  }

  // Detect cycles by walking parent chain for each node
  /** @type {Set<string>} */
  const cycleDropped = new Set();

  for (const item of items) {
    if (item.parent_id === null) continue;

    const visited = new Set();
    /** @type {string[]} */
    const chain = [item.id];
    /** @type {string | null} */
    let current = item.parent_id;

    while (current !== null) {
      if (visited.has(current)) {
        // Cycle: drop this node
        chain.push(current);
        cycleDropped.add(item.id);
        if (onWarning) {
          onWarning('Cycle detected', { nodeId: item.id, parentChain: chain });
        }
        break;
      }
      visited.add(current);
      chain.push(current);
      const parent = itemMap.get(current);
      current = parent?.parent_id ?? null;
    }
  }

  // Group children by parent_id, excluding cycle-dropped nodes
  /** @type {Map<string | null, import('../../contracts/menu.js').MenuItem[]>} */
  const childrenOf = new Map();

  for (const item of items) {
    if (cycleDropped.has(item.id)) continue;

    // Check for orphans (parent_id references non-existent node)
    if (item.parent_id !== null && !itemMap.has(item.parent_id)) {
      if (onWarning) {
        onWarning('Orphan node', { nodeId: item.id, parentId: item.parent_id });
      }
      continue;
    }

    const parentKey = item.parent_id;
    if (!childrenOf.has(parentKey)) {
      childrenOf.set(parentKey, []);
    }
    /** @type {import('../../contracts/menu.js').MenuItem[]} */ (childrenOf.get(parentKey)).push(item);
  }

  /**
   * @param {string | null} parentId
   * @returns {import('../../contracts/menu.js').MenuNode[]}
   */
  function buildLevel(parentId) {
    const children = childrenOf.get(parentId) || [];
    return children
      .slice()
      .sort((a, b) => a.order - b.order)
      .map(item => ({
        id: item.id,
        parent_id: item.parent_id,
        order: item.order,
        label: item.label,
        path: item.path,
        ...(item.icon_id !== undefined && { icon_id: item.icon_id }),
        ...(item.icon_url !== undefined && { icon_url: item.icon_url }),
        ...(item.permission !== undefined && { permission: item.permission }),
        children: buildLevel(item.id),
      }));
  }

  return buildLevel(null);
}
